import { type TOptions, useTranslation } from '@gympass/i18n';
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import { PageSkeleton } from 'components/templates/PageSkeleton';
import type { Language } from 'services/i18n';

import localizationNamespaces from '../../../localization-namespaces.json';

type TranslationOptions = TOptions<Record<string, unknown>> & {
  defaultValue?: string;
  context?: string;
};

export interface LocalizationContext {
  language: Language;
}

export const LocalizationContext = createContext<LocalizationContext>({
  language: 'en',
});

export function LocalizationProvider({ children }: PropsWithChildren) {
  const { i18n, ready } = useTranslation(localizationNamespaces.namespaces);

  const value = useMemo(() => {
    return { language: i18n.language as Language };
  }, [i18n.language]);

  return (
    <LocalizationContext.Provider value={value}>
      {!ready ? <PageSkeleton /> : children}
    </LocalizationContext.Provider>
  );
}
export interface UseLocalization extends LocalizationContext {
  t: (key: string, values?: TranslationOptions) => string;
}

export function useLocalization(context?: string): UseLocalization {
  const value = useContext(LocalizationContext);
  const { t: translate } = useTranslation();

  const t = useCallback(
    (key: string, options?: TranslationOptions) =>
      translate(context ? `${context}.${key}` : key, {
        ...options,
        defaultValue: options?.defaultValue ?? undefined,
        interpolation: {
          escapeValue: false,
        },
      }),
    [context, translate],
  );

  return { ...value, t };
}
